<template>
  <div class="container-fluid">
    <div class="card card-default">
      <div class="card-header feature-header">
        <div class="row">
          <div class="col-auto me-auto">
            <CardTitleComponent />
          </div>
          <div class="col-auto">
            <SearchComponent v-model="searchText" @search="onSearch" @clear="clearSearch" />
          </div>
          <div class="col-auto">
            <SubscriptionCommonComponent ref="subscriptionComponentRef" v-if="isHost()"
              @on-subscription="onSubscriptionChange" />
          </div>
          <div class="col-auto">
            <a class="btn btn-outline-primary" :class="{ disabled: isHost() && !selectedSubscription?.id }" @click="(!isHost() || selectedSubscription?.id) && open()">
              <i class="fas fa-plus me-1"></i>{{ $t('addNew') }}
            </a>
          </div>
        </div>
      </div>
      <div class="card-body p-2">
        <div class="card-body-grid">
          <ejs-grid ref="gridRole" :dataSource="data" :allowPaging="true" :allowResizing="true"
            :pageSettings="pageSettings" :locale="locale" :rowHeight="38" :allowSorting="true" height="100%"
            :searchSettings="searchOptions" :dataStateChange="dataStateChange"
            :queryCellInfo="queryCellInfo">
            <e-columns>
              <e-column v-if="isHost()" :headerText="$t('organization')"
                field="Subscription.OrganizationName"></e-column>
              <e-column :headerText="$t('role')" field="Name"></e-column>
              <e-column :headerText="$t('role') + ' ' + $t('displayName')" field="DisplayName"></e-column>
              <e-column :headerText="$t('actions')" width="75" textAlign="Center" :template="'actionTemplate'">
                <template v-slot:actionTemplate="{ data }">
                  <div class="btn-group actions-row" role="group" aria-label="Basic example">
                    <a href="javascript:void(0)" class="ms-2 action-link action-edit" title="Edit"
                      @click="commandClick('Edit', data)">
                      <i class="e-edit e-icons" style="font-size: 0.76em;"></i>
                    </a>
                    <a href="javascript:void(0)" class="ms-2 action-link action-delete" title="Delete"
                      @click="commandClick('Delete', data)">
                      <i class="e-delete e-icons" style="font-size: 0.76em;"></i>
                    </a>
                  </div>
                </template>
              </e-column>
            </e-columns>
          </ejs-grid>
        </div>
      </div>
    </div>
  </div>
  <div class="modal fade" id="roleModal" role="dialog">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h4 v-if="isHost()" class="modal-title">
            {{ $t('role') }}
            <span v-if="role.organizationName">- {{ role.organizationName }}</span>
          </h4>
          <h4 v-else class="modal-title">{{ $t('role') }}</h4>
          <button type="button" class="close" data-bs-dismiss="modal" aria-label="Close" @click="close()">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="row">
            <div class="col-8">
              <div class="input-group input-group-sm">
                <div class="input-group-prepend">
                  <span class="input-group-text" id="basic-addon1">
                    {{ $t('role') + ' ' + $t('name') }} &nbsp;<span class="text-red">*</span></span>
                </div>
                <input type="text" class="form-control form-control-sm" v-model="role.displayName" data-pi=""
                  autocomplete="off" />
              </div>
            </div>
            <div class="col-4">
              <div class="btn-group btn-group-sm" role="group" aria-label="Basic example">
                <button class="btn btn-default btn-sm" style="font-size: 0.76em; font-weight: 500"
                  @click="onExpandCollapseAll">
                  <i :class="treeManage.isExpand
                    ? 'fas fa-expand me-2'
                    : 'fas fa-compress me-2'
                    "></i>{{ !treeManage.isExpand ? 'Expand All' : 'Collapse All' }}
                </button>
                <button class="btn btn-default btn-sm" style="font-size: 0.76em; font-weight: 500"
                  @click="onSelectUnselectAll">
                  <i :class="treeManage.isSelect
                    ? 'fas fa-check-circle me-2'
                    : 'fas fa-circle me-2'
                    "></i>{{ treeManage.isSelect ? 'Select All' : 'Unselect All' }}
                </button>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-sm-12">
              <div class="modal-responsive">
                <CustomTreeView ref="treeviewPage" :dataSource="rolePages"
                  :fields="{ id: 'id', pid: 'pid', text: 'name', hasChildren: 'hasChild' }" v-model="selectedTreeNodes">
                </CustomTreeView>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-sm btn-outline-close" @click="close()">
            <i class="fas fa-times"></i>&nbsp;{{ $t('close') }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-info-reverse" @click="onUpsert()">
            <i class="fas fa-save"></i>&nbsp;{{ isUpdate ? $t('update') : $t('save') }}
          </button>
        </div>
      </div>
      <!-- /.modal-content -->
    </div>
    <!-- /.modal-dialog -->
  </div>
</template>

<script lang="ts">
import './PermissionComponent.css';
import {
  GridComponent as EjsGrid,
  ColumnsDirective as EColumns,
  ColumnDirective as EColumn,
  Resize,
  Toolbar,
  Page,
  Group,
  Sort,
} from '@syncfusion/ej2-vue-grids'
import { defineComponent } from 'vue'
import modal from '@/components/common/modalManage'
import agent, { masterServiceBaseURL } from '@/services/agent'
import toasterService from '@/services/toasterService'
import { L10n, setCulture } from '@syncfusion/ej2-base'
import authService from '@/services/authService'
import _ from 'lodash'
import {
  GetRoleWisePagePermission,
  Property,
  Role,
  RoleGetResponse,
  RolePageGetResponse,
  RolePagesResponse,
} from './Permission.model'
import SubscriptionCommonComponent from '../Subscription/SubscriptionCommonComponent.vue'
import { Subscription } from '../Subscription/Subscription.model'
import CustomTreeView from '@/components/shared/treeview/CustomTreeView.vue';
import { API_KEY } from './constants'
import { DialogUtility } from '@syncfusion/ej2-vue-popups'
import emitter from '@/services/mitt'
import CardTitleComponent from '@/components/common/CardTitleComponent.vue';
import SearchComponent from '@/components/shared/SearchComponent.vue';

let _roleData: Property = {} as Property
let _subscription = {} as Subscription | undefined
let Confirmation: any = undefined

export default defineComponent({
  components: {
    'ejs-grid': EjsGrid,
    'e-columns': EColumns,
    'e-column': EColumn,
    CustomTreeView,
    SubscriptionCommonComponent,
    CardTitleComponent,
    SearchComponent,
  },
  provide: {
    grid: [Toolbar, Resize, Page, Sort, Group],
  },
  created() {
    agent.setBaseUrl(masterServiceBaseURL)

    this.gridLocalize()
  },
  data() {
    return {
      data: _roleData,
      rolePages: [] as GetRoleWisePagePermission[],
      role: {} as Role,
      isUpdate: false,
      isCheckedAll: false,
      selectedState: null,
      subscriptionId: '',
      selectedSubscription: _subscription as Subscription,
      searchOptions: {
        fields: ['Name', 'DisplayName'],
        operator: 'contains',
        ignoreCase: true,
      },
      searchText: '',
      pageSettings: {
        pageSizes: [10, 20, 50, 100],
        pageSize: 50,
        pageCount: 5,
        skip: 0,
        take: 50,
      },
      treeManage: {
        isExpand: false,
        isSelect: false,
        treeSource: {
          fields: {
            dataSource: [] as GetRoleWisePagePermission[],
            id: 'id',
            parentID: 'pid',
            text: 'name',
            hasChildren: 'hasChild',
          },
        },
      },
      selectedTreeNodes: [] as any[],
      locale: '',
    }
  },
  mounted() {
    this.dataStateChange(
      this.initState(this.pageSettings.skip, this.pageSettings.take)
    )
  },
  methods: {
    treeView() {
      const treeView: any = this.$refs.treeviewPage;
      return treeView;
    },
    initState(skip: number = 0, take: number): any {
      let state = {
        skip: skip,
        take: take,
      }
      return state
    },
    dataStateChange(state: any) {
      this.pageSettings.skip = state.skip
      this.pageSettings.take = state.take
      this.gridLocalize()
      this.loadRole(state)
    },
    async loadRole(state: any) {
      if (state.action) {
        delete state.action
      }

      var subscriptionId = this.subscriptionId
      const gridRole: any = this.$refs.gridRole
      if (gridRole) gridRole.hideSpinner()

      const response = await agent.post<RoleGetResponse>(API_KEY.GET_ROLE, {
        ...state,
        subscriptionId,
      })
      if (response && response.result) {
        this.data = response.result
      }
    },
    onUpsert() {
      if (this.isUpdate) {
        this.onEdit()
      } else {
        this.onSave()
      }
    },
    checkingPages() {
      if (this.treeView()) {
        const checkedAll = this.treeView().getAllCheckedNodes()
        _.map(this.rolePages, (item) => {
          item.isChecked = checkedAll.some((id: any) => Number(id) == item.id)
        })
      }
    },
    async onSave() {
      this.checkingPages()

      this.role.name = this.role.displayName
      const post = {
        role: this.role,
        rolePages: this.rolePages,
        createdOrModifiedBy: authService.getUserEmail(),
        subscriptionId: this.selectedSubscription?.id,
      }

      if (_.isEmpty(post.role.name)) {
        toasterService.warn(this.$t('msgGiveRole'))
        return
      }

      if (!post.rolePages.some((i) => i.isChecked == true)) {
        toasterService.warn(this.$t('msgSelectOnePageAtLeast'))
        return
      }

      const response = await agent.post<RolePagesResponse>(API_KEY.POST, post)

      if (response && response.result) {
        this.loadRole(
          this.initState(this.pageSettings.skip, this.pageSettings.take)
        )
        toasterService.success(this.$t('msgSave'))
        this.close()
        emitter.emit('update-permission', {})
      }
    },
    async onEdit() {
      this.checkingPages()

      this.role.name = this.role.displayName
      const put = {
        role: this.role,
        rolePages: this.rolePages,
        createdOrModifiedBy: authService.getUserEmail(),
      }

      if (_.isEmpty(put.role)) {
        toasterService.warn(this.$t('msgGiveRole'))
        return
      }

      if (!put.rolePages.some((i) => i.isChecked == true)) {
        toasterService.warn(this.$t('msgSelectOnePageAtLeast'))
        return
      }

      const response = await agent.put<RolePagesResponse>(API_KEY.PUT, put)
      if (response && response.result) {
        this.loadRole(
          this.initState(this.pageSettings.skip, this.pageSettings.take)
        )
        toasterService.success(this.$t('msgUpdate'))
        this.close()
        emitter.emit('update-permission', {})
      }
    },
    async onDelete(id: number) {
      const response = await agent.delete<RolePagesResponse>(
        API_KEY.DELETE_ROLE_PAGE(id)
      )
      if (response && response.result) {
        this.loadRole(
          this.initState(this.pageSettings.skip, this.pageSettings.take)
        )
        toasterService.success(this.$t('msgDelete'))
        this.close()
        emitter.emit('update-permission', {})
      }
    },
    async loadRolePage(
      id: number,
      subscriberType?: string,
      subscriptionId?: string
    ) {
      const payload = {
        roleId: id,
        subscriberType: subscriberType,
        subscriptionId: subscriptionId,
      }
      const response = await agent.get<RolePageGetResponse>(
        API_KEY.GET,
        payload
      )

      if (response && response.result) {
        this.rolePages = response.result
        this.treeManage.isExpand = false
        this.treeManage.isSelect = false

        // Initialize selected nodes
        this.selectedTreeNodes = this.rolePages.filter(p => p.isChecked).map(p => p.id);

        this.$nextTick(() => {
          if (this.treeView()) {
            this.treeView().collapseAll();
          }
        });

        modal.Open('roleModal')
      }
    },
    onExpandCollapseAll() {
      this.treeManage.isExpand = !this.treeManage.isExpand
      if (this.treeView()) {
        if (this.treeManage.isExpand) {
          this.treeView().expandAll()
        } else {
          this.treeView().collapseAll()
        }
      }
    },
    onSelectUnselectAll() {
      this.treeManage.isSelect = !this.treeManage.isSelect
      if (this.treeView()) {
        if (this.treeManage.isSelect) {
          this.treeView().checkAll()
        } else {
          this.treeView().uncheckAll()
        }
      }
    },
    onSubscriptionChange(subscriptionId: string) {
      this.subscriptionId = subscriptionId
      if (subscriptionId.trim().toLowerCase().indexOf('all') > -1) {
        this.subscriptionId = ''
      }

      this.loadRole(
        this.initState(this.pageSettings.skip, this.pageSettings.take)
      )

      const subscriptionComponent: any = this.$refs.subscriptionComponentRef
      const subscription = (subscriptionComponent.data as Subscription[]).find(
        (s) => s.id === subscriptionId
      ) as Subscription
      this.selectedSubscription = subscription
    },
    resetRole() {
      this.role = {} as Role
      this.rolePages = []
      this.isUpdate = false
    },
    gridLocalize() {
      this.locale = 'en-grid'
      if (this.$i18n.locale == 'se') {
        setTimeout(() => {
          import(`@/assets/sv.json`).then((module) => {
            const localText = module.default
            this.locale = 'sv'
            setCulture('sv')
            L10n.load(localText)
          })
        })
      }
    },
    commandClick(type: string, data: Role) {
      this.isUpdate = true
      const app = this
      switch (type) {
        case 'Delete':
          this.role.id = data.id
          Confirmation = DialogUtility.confirm({
            title: this.$t('deleteConfiramtion'),
            content: this.$t('msgSureWantToDelete'),
            okButton: {
              text: this.$t('ok'),
              click: async function () {
                Confirmation.hide()
                app.onDelete(app.role.id)
              },
            },
            cancelButton: { text: this.$t('cancel') },
            showCloseIcon: true,
            closeOnEscape: true,
            zIndex: 10000,
            animationSettings: { effect: 'Zoom' },
          })
          break
        case 'Edit':
          this.role = data
          this.loadRolePage(
            this.role.id,
            this.role.subscriberType,
            this.role.subscriptionId
          )
          break
        default:
          break
      }
    },
    resetRolePage() {
      _.map(this.rolePages, (item) => {
        item.isChecked = false
      })
      this.role = {} as Role
      this.isUpdate = false
      this.isCheckedAll = false
      this.role.organizationName = String(
        this.selectedSubscription?.organizationName
      )
    },
    isHost() {
      return authService.isHost()
    },
    open() {
      this.resetRolePage()
      this.loadRolePage(
        0,
        this.selectedSubscription.subscriberType,
        this.selectedSubscription.id
      )
    },
    close() {
      this.resetRolePage()
      modal.Close('roleModal')
    },
    onSearch() {
      const grid = (this.$refs.gridRole as any)?.ej2Instances;
      if (grid) grid.search(this.searchText);
    },
    clearSearch() {
      this.searchText = '';
      const grid = (this.$refs.gridRole as any)?.ej2Instances;
      if (grid) grid.search('');
    },
    queryCellInfo(args: any) {
      if (args.column.headerText === this.$t('actions')) {
        const isStatic = args.data.isStatic;
        if (isStatic && !this.isHost()) {
          const menu = args.cell.querySelector('.e-grid-menu');
          if (menu) menu.style.visibility = 'hidden';
        } else if (isStatic) {
          const deleteBtn = args.cell.querySelector('button[title="Delete"]');
          if (deleteBtn) deleteBtn.style.visibility = 'hidden';
        }
      }
    },
  },
})
</script>
